import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, ChevronRight, Search, Loader2 } from 'lucide-react';
import { getContractors, SEED_CONTRACTORS } from '../services/contractors';
import { Contractor } from '../types';

export default function ContractorsList() {
    const [contractors, setContractors] = useState<Contractor[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadContractors();
    }, []);

    const loadContractors = async () => {
        try {
            const data = await getContractors();
            setContractors(data.length > 0 ? data : SEED_CONTRACTORS);
        } catch (err) {
            console.error('Error loading contractors:', err);
            setContractors(SEED_CONTRACTORS);
        } finally {
            setLoading(false);
        }
    };

    const filteredContractors = contractors.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.displayName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6 fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-primary">Contractors</h1>
                    <p className="text-secondary text-sm mt-1">
                        Manage contractor profiles and their search preferences
                    </p>
                </div>
                <button className="btn btn-primary">
                    <Plus className="w-4 h-4" />
                    Add Contractor
                </button>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
                <input
                    type="text"
                    placeholder="Search contractors..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field pl-10"
                />
            </div>

            {/* Contractors Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredContractors.map((contractor) => (
                    <Link
                        key={contractor.id}
                        to={`/contractor/${contractor.id}`}
                        className="card p-5 hover:border-blue-500/50 group"
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-tertiary flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-secondary" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-primary group-hover:text-blue-400 transition-colors">
                                        {contractor.displayName}
                                    </h3>
                                    <p className="text-xs text-tertiary">{contractor.name}</p>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-tertiary group-hover:text-primary transition-colors" />
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                            <span className="badge badge-default">
                                {contractor.naicsCodes.length} NAICS
                            </span>
                            <span className="badge badge-default">
                                {contractor.keywords.length} Keywords
                            </span>
                        </div>

                        {contractor.markets && contractor.markets.length > 0 && (
                            <p className="mt-3 text-xs text-tertiary line-clamp-1">
                                {contractor.markets.slice(0, 3).join(' • ')}
                            </p>
                        )}
                    </Link>
                ))}
            </div>

            {filteredContractors.length === 0 && (
                <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-tertiary mx-auto mb-4" />
                    <p className="text-secondary">No contractors found</p>
                </div>
            )}
        </div>
    );
}
